import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Clock, History, ShieldCheck, User, XCircle } from 'lucide-react';
import { apiGet } from '../../lib/api.js';
import { SYNC_SOURCES, fmtCompletedAt, statusBadge, targetLabel } from '../../lib/syncHistory.js';

const TONES = {
  emerald: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  amber: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  rose: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  slate: 'bg-white/10 text-white/60 border-white/15',
};
const BADGE_ICON = { Synced: CheckCircle2, Pending: Clock, Declined: XCircle, Cancelled: XCircle };

/**
 * Immutable log of the caller's own sync requests (master + manager).
 * @returns {JSX.Element}
 */
export default function SyncHistoryTable() {
  const { data, isLoading } = useQuery({
    queryKey: ['sync-history'],
    queryFn: () => apiGet('/sync/history'),
    staleTime: 10_000,
    refetchInterval: 15_000,
  });
  const rows = data?.history || [];

  return (
    <div className="panel p-6 space-y-3 border border-white/10 bg-[#0d0714] rounded-2xl" data-testid="sync-history">
      <h3 className="font-display text-lg font-bold text-white flex items-center gap-2">
        <History className="w-5 h-5 text-civix-purpleSoft" /> Completed Sync Requests
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-white/50 text-xs border-b border-white/10">
              <th className="py-2 pr-4 font-medium">To</th>
              <th className="py-2 pr-4 font-medium">Status</th>
              <th className="py-2 font-medium">Completed at</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {isLoading && <tr><td colSpan={3} className="py-6 text-center text-white/40 text-xs">Loading…</td></tr>}
            {!isLoading && rows.length === 0 && (
              <tr><td colSpan={3} className="py-8 text-center text-white/40 text-xs" data-testid="sync-history-empty">No syncs yet. Clone a master package or send a request to get started.</td></tr>
            )}
            {rows.map((r) => {
              const badge = statusBadge(r.status);
              const Icon = BADGE_ICON[badge.label];
              const TypeIcon = r.sourceType === SYNC_SOURCES.MASTER ? ShieldCheck : User;
              return (
                <tr key={r._id} data-testid="sync-history-row">
                  <td className="py-3 pr-4 text-white/90">
                    <span className="inline-flex items-center gap-2"><TypeIcon className="w-4 h-4 text-civix-purpleSoft shrink-0" />{targetLabel(r)}</span>
                  </td>
                  <td className="py-3 pr-4">
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${TONES[badge.tone]}`}>
                      {Icon && <Icon className="w-3 h-3" />}{badge.label}
                    </span>
                  </td>
                  <td className="py-3 text-white/70 whitespace-nowrap">{fmtCompletedAt(r.completedAt)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
